import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { CuteLoader } from "@/components/CuteLoader";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;

export const CommissionExplainer = ({ x }) => (
  <div className="py-[12px] pl-4 border-b border-[#EAE1D4] text-[13px] text-[#6B5442]" data-testid="commission-explainer">
    <div className="flex justify-between" data-testid="commission-explainer-paid">
      <span>Paid per ADP</span>
      <span className="text-[#231E1A]">{money(x.adp_paid)}</span>
    </div>
  </div>
);


export default function PaydayRecap() {
  const { periodId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [taxOpen, setTaxOpen] = useState(false);

  const fetchRecap = useCallback(async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    try {
      const { data: recapData } = await api.get(`/employee/payday-recap/${periodId}`, {
        signal: controller.signal
      });
      setData(recapData);
      setError(null);
    } catch (error) {
      console.error('Error:', error);
      if (error.name === 'AbortError') {
        setError('Request timed out');
      } else {
        setError('Could not load recap');
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, [periodId]);

  useEffect(() => {
    fetchRecap();
  }, [fetchRecap]);

  if (loading) {
    return <CuteLoader />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAF7F1] flex items-center justify-center px-6">
        <div className="text-center">
          <div className="text-[#9C6B4A] text-lg mb-4" style={{ fontFamily: 'Cormorant, serif' }}>
            {error}
          </div>
          <button
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-[#9C6B4A] text-white rounded-lg hover:bg-[#8B5A39] transition"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const { period, employee, gross_pay, breakdown, take_home, comparison, fun_fact, payday_number, milestone_phrase } = data;
  
  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const fmtEyebrow = (iso) => { const [y, m, d] = iso.slice(0, 10).split('-').map(Number); return `${monthNames[m - 1].toUpperCase()} ${d}, ${y}`; };
  const eyebrowDate = (data.paid_check_dates || [period.check_date]).map(fmtEyebrow).join(' & ');

  return (
    <div className="min-h-screen bg-[#FAF7F1]">
      {/* Header */}
      <div className="bg-gradient-to-br from-[#231E1A] to-[#3A2A1E] px-6 pt-[58px] pb-8 relative">
        <button 
          onClick={() => navigate('/employee/home')}
          className="absolute top-4 right-4 text-white/60 hover:text-white"
        >
          <X size={24} />
        </button>
        
        <div className="text-[11px] tracking-[0.15em] uppercase text-[#B0905E] mb-3">
          PAYDAY RECAP · {eyebrowDate}
        </div>
        
        <div className="font-serif text-[30px] leading-tight text-white">
          The money landed, <em>gorgeous.</em>
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-6 space-y-4">
        {/* Card 1: Gross Amount */}
        <div className="bg-[#F3EEE7] rounded-[14px] p-6 text-center">
          <div className="text-[10px] tracking-[0.1em] uppercase text-[#9C6B4A]/60 mb-3">
            This period · Finalized
          </div>
          <div className="font-serif text-[44px] leading-none text-[#231E1A]">
            ${Math.floor(gross_pay)}<span className="text-[22px]">.{(gross_pay % 1).toFixed(2).slice(2)}</span>
          </div>
          <div className="text-[13px] text-[#9C6B4A] mt-2">
            {breakdown.hours.toFixed(1)} hours · payday #{payday_number}
            {milestone_phrase && ` ${milestone_phrase}`}
          </div>
        </div>

        {/* Card 2: Breakdown */}
        <div className="bg-[#F3EEE7] rounded-[14px] p-6">
          {breakdown.hourly > 0 && (
            <div className="flex justify-between py-[14px] border-b border-[#EAE1D4]">
              <span className="text-[14px] text-[#3A2A1E]">Hourly</span>
              <span className="text-[14px] text-[#231E1A]">${breakdown.hourly.toFixed(2)}</span>
            </div>
          )}
          
          {breakdown.service_commission > 0 && (
            <div className="flex justify-between py-[14px] border-b border-[#EAE1D4]">
              <span className="text-[14px] text-[#3A2A1E]">Service commission</span>
              <span className="text-[14px] text-[#231E1A]">${breakdown.service_commission.toFixed(2)}</span>
            </div>
          )}
          {data.commission_explainer && <CommissionExplainer x={data.commission_explainer} />}
          
          {breakdown.contractor_1099 > 0 && (
            <div className="flex justify-between py-[14px] border-b border-[#EAE1D4]" data-testid="recap-1099-row">
              <span className="text-[14px] text-[#3A2A1E]">1099 pay</span>
              <span className="text-[14px] text-[#231E1A]">${breakdown.contractor_1099.toFixed(2)}</span>
            </div>
          )}

          {breakdown.product_commission > 0 && (
            <div className="flex justify-between py-[14px] border-b border-[#EAE1D4]">
              <span className="text-[14px] text-[#3A2A1E]">Product commission</span>
              <span className="text-[14px] text-[#231E1A]">${breakdown.product_commission.toFixed(2)}</span>
            </div>
          )}
          
          {breakdown.tips > 0 && (
            <div className="flex justify-between py-[14px] border-b border-[#EAE1D4] text-[#9C6B4A] font-semibold">
              <span className="text-[14px]">Tips, on top</span>
              <span className="text-[14px]">${breakdown.tips.toFixed(2)}</span>
            </div>
          )}
          
          {breakdown.reimbursement > 0 && (
            <div className="flex justify-between py-[14px] border-b border-[#EAE1D4]" data-testid="recap-reimbursement-row">
              <span className="text-[14px] text-[#3A2A1E]">Reimbursement</span>
              <span className="text-[14px] text-[#231E1A]">${breakdown.reimbursement.toFixed(2)}</span>
            </div>
          )}

          {breakdown.hra > 0 && (
            <div className="flex justify-between py-[14px] border-b border-[#EAE1D4]" data-testid="recap-hra-row">
              <span className="text-[14px] text-[#3A2A1E]">Health reimbursement (HRA)</span>
              <span className="text-[14px] text-[#231E1A]">${breakdown.hra.toFixed(2)}</span>
            </div>
          )}

          {breakdown.pay_vs_sales_percent && (
            <div className="flex justify-between py-[14px] border-b border-[#EAE1D4]">
              <span className="text-[14px] text-[#3A2A1E]">Your pay + commission vs your sales</span>
              <span className="text-[14px] text-[#231E1A]">{breakdown.pay_vs_sales_percent}%</span>
            </div>
          )}
          
          {breakdown.pto_earned > 0 && (
            <div className="flex justify-between py-[14px]">
              <span className="text-[14px] text-[#3A2A1E]">PTO earned</span>
              <span className="text-[14px] text-[#231E1A]">+{breakdown.pto_earned.toFixed(2)} hrs</span>
            </div>
          )}
        </div>

        {/* Card 2b: Take-home (verified-data-only) */}
        {take_home && take_home.variant === 'employee' && (
          <div className="bg-[#F3EEE7] rounded-[14px] p-6" data-testid="take-home-card">
            <button
              onClick={() => setTaxOpen(v => !v)}
              className="w-full flex justify-between items-center py-[14px] border-b border-[#EAE1D4]"
              data-testid="taxes-withheld-toggle"
            >
              <span className="text-[14px] text-[#3A2A1E]">Taxes withheld{take_home.withholdings?.length ? (taxOpen ? ' ▾' : ' ▸') : ''}</span>
              <span className="text-[14px] text-[#9C6B4A]" data-testid="taxes-withheld-total">−${take_home.total_withheld.toFixed(2)}</span>
            </button>
            {taxOpen && (take_home.withholdings || []).map((w, i) => (
              <div key={i} className="flex justify-between py-[10px] pl-4 border-b border-[#EAE1D4]/60" data-testid={`withholding-row-${i}`}>
                <span className="text-[13px] text-[#3A2A1E]/70">{w.label}</span>
                <span className="text-[13px] text-[#3A2A1E]/70">−${w.amount.toFixed(2)}</span>
              </div>
            ))}
            <div className="flex justify-between items-center pt-[16px]" data-testid="net-pay-row">
              <span className="text-[14px] text-[#9C6B4A] font-semibold">What landed in your account</span>
              <span className="font-serif text-[22px] text-[#231E1A]">${take_home.net_pay.toFixed(2)}</span>
            </div>
          </div>
        )}

        {/* Card 2b: Contractor education line */}
        {take_home && take_home.variant === 'contractor' && (
          <div className="bg-[#F3EEE7] rounded-[14px] p-6 border-l-[3px] border-[#B0905E]" data-testid="contractor-tax-note">
            <div className="font-serif italic text-[16px] leading-[1.5] text-[#3A2A1E]">
              {take_home.note}
            </div>
          </div>
        )}
        {data.pto_paid_note && (
          <div className="bg-[#F3EEE7] rounded-[14px] p-6" data-testid="recap-pto-paid-note">
            <div className="font-serif italic text-[18px] leading-[1.4] text-[#3A2A1E]">{data.pto_paid_note}</div>
            <div className="text-[11px] tracking-[0.15em] uppercase text-[#9C6B4A] mt-2">Source: ADP</div>
          </div>
        )}
            {data.time_off_note && (
          <div className="bg-[#F3EEE7] rounded-[14px] p-6" data-testid="recap-time-off-note">
            <div className="font-serif italic text-[18px] leading-[1.4] text-[#3A2A1E]">
              {data.time_off_note}
            </div>
          </div>
        )}
        {comparison && comparison.explanation && (
          <div className="bg-[#F3EEE7] rounded-[14px] p-6 border-l-[3px] border-[#B0905E]">
            <div className="text-[11px] tracking-[0.15em] uppercase text-[#9C6B4A] mb-2">
              VS LAST PERIOD
            </div>
            <div className="font-serif italic text-[18px] leading-[1.4] text-[#3A2A1E]">
              {comparison.explanation}
            </div>
          </div>
        )}

        {/* Card 4: Fun Fact */}
        {fun_fact && (
          <div className="bg-[#F3EEE7] rounded-[14px] p-6">
            <div className="text-[11px] tracking-[0.15em] uppercase text-[#B0905E] mb-2">
              FUN FACT
            </div>
            <div className="font-serif text-[18px] leading-[1.4] text-[#3A2A1E]">
              {fun_fact}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
